import { Children, ReactNode, cloneElement, isValidElement } from 'react';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { CardGroup } from '@/common/components/CardGroup/CardGroup';
import { Heading } from '@/common/components/Heading/Heading';
import { TRadioButtonOptionProps } from '@/common/components/RadioButton/RadioButtonOption/RadioButtonOption';
import { Stack } from '@/common/components/Stack/Stack';
import { TGap } from '@/common/theme/variants';
import { Root } from '@rn-primitives/radio-group';
import { styles } from './RadioButtonGroup.styles';

export const RadioButtonGroup = ({
  title,
  description,
  gap = 'md',
  variant,
  children,
  value,
  hint,
  error,
  disabled,
  onChange,
  className,
}: IRadioButtonGroupProps) => {
  const Container = variant === 'card' ? CardGroup : Stack;
  const message = error ?? hint;

  return (
    <Stack gap="md">
      {title || description ? (
        <Stack gap="xs">
          {title && <Heading level={4}>{title}</Heading>}
          {description && (
            <BodyText size="sm" color="neutral30">
              {description}
            </BodyText>
          )}
        </Stack>
      ) : null}
      <Root value={value} onValueChange={onChange} disabled={disabled}>
        <Container
          gap={variant === 'card' ? '2xs' : gap}
          className={styles.host({
            error: Boolean(error && variant === 'card'),
            className,
          })}
        >
          {Children.map(children, (child, _idx) => {
            if (
              isValidElement<
                TRadioButtonOptionProps &
                  Required<Pick<TRadioButtonOptionProps, 'value'>>
              >(child)
            ) {
              return cloneElement<
                TRadioButtonOptionProps &
                  Required<Pick<TRadioButtonOptionProps, 'value'>>
              >(child, {
                error: false,
                disabled: disabled || child.props.disabled,
                variant,
                onPress: () => onChange(child.props.value),
                checked: child.props.value === value,
              });
            }
            return child;
          })}
        </Container>
      </Root>
      {message && (
        <BodyText size="sm" color={error ? 'systemError' : undefined}>
          {message}
        </BodyText>
      )}
    </Stack>
  );
};

export interface IRadioButtonGroupProps {
  variant?: 'plain' | 'card';
  title?: string;
  description?: ReactNode;
  value?: string;
  children: ReactNode;
  disabled?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
  onChange: (value: string) => void;
  gap?: TGap;
}
